/**
 * Payment recording dialog. Used from the party-ledger screen.
 *
 * Two modes:
 *   - two-party (default): payer + payee + amount + method + note
 *   - three-party `via_self`: explicit checkbox + visible warning panel
 *     that describes what will hit each ledger, before submit
 *
 * Attachments are uploaded after the payment is created (so the backend
 * can resolve ledger-entry ids and attach to the right side). For now we
 * attach the file to the PAYER-side entry as the default — users can
 * re-attach to the payee side from the party ledger screen if they need.
 */
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { LedgerEntry, Party, Payment, PaymentMethod } from '../api';
import {
  Button,
  Card,
  Checkbox,
  ErrorBanner,
  Pill,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../ui/primitives';
import { inrToKinrString, inrToKinrPreview } from '../money/inr';
import { uploadFile, UploadError } from '../lib/uploadFile';
import { Modal } from '../ui/Modal';

// Per-file upload status surface for the dialog. Each selected file gets
// one of these states independently, so a failure on file N doesn't lose
// files N+1…M and the user can retry only the failed files.
type FileStatus =
  | { kind: 'pending' }
  | { kind: 'uploading'; pct: number }
  | { kind: 'done'; id: number }
  | { kind: 'failed'; message: string };

interface FileEntry {
  uid: string; // stable React key
  file: File;
  status: FileStatus;
}

const METHODS: PaymentMethod[] = ['cash', 'bank_transfer', 'upi', 'cheque', 'other'];

// localStorage key for remembering the user's last-used payment options
// across dialog openings. Amounts, notes, and effective date are NEVER
// persisted (they always start fresh). Payer/payee are persisted only when
// the caller hasn't pre-selected them via props.
const PREFS_KEY = 'paymentDialog.prefs.v1';

interface PaymentDialogPrefs {
  method?: PaymentMethod;
  viaSelf?: boolean;
  lastPayerPartyId?: string;
  lastPayeePartyId?: string;
}

function readPrefs(): PaymentDialogPrefs {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writePrefs(p: PaymentDialogPrefs): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    // ignore quota errors
  }
}

/**
 * Prefill values for the "Void & re-enter" flow: the dialog opens with the
 * voided payment's values so the owner only fixes what was wrong. Amount is
 * a plain-INR string (the input's unit). Prefills win over remembered prefs.
 */
export interface PaymentInitialValues {
  payerPartyId?: number;
  payeePartyId?: number;
  amountInr?: string;
  method?: PaymentMethod;
  viaSelf?: boolean;
  effectiveDate?: string;
  note?: string;
}

export interface PaymentDialogProps {
  /** If provided, the payee dropdown is pre-selected and usually locked. */
  preselectPayeeId?: number | null;
  /** If provided, the payer dropdown is pre-selected. */
  preselectPayerId?: number | null;
  /** Prefill for re-entry after a void (shows a hint banner when set). */
  initial?: PaymentInitialValues;
  onClose: () => void;
  onCreated: (payment: Payment) => void;
}

export function PaymentDialog({
  preselectPayeeId,
  preselectPayerId,
  initial,
  onClose,
  onCreated,
}: PaymentDialogProps) {
  const qc = useQueryClient();
  const parties = useQuery<Party[]>({
    queryKey: ['parties'],
    queryFn: () => api.get<Party[]>('/api/parties'),
  });

  // Read remembered preferences once at mount. Caller-supplied preselects
  // always win over remembered values — prefs only fill the gaps.
  const prefs = useMemo(() => readPrefs(), []);

  const [payerPartyId, setPayerPartyId] = useState(
    initial?.payerPartyId
      ? String(initial.payerPartyId)
      : preselectPayerId
        ? String(preselectPayerId)
        : prefs.lastPayerPartyId ?? '',
  );
  const [payeePartyId, setPayeePartyId] = useState(
    initial?.payeePartyId
      ? String(initial.payeePartyId)
      : preselectPayeeId
        ? String(preselectPayeeId)
        : prefs.lastPayeePartyId ?? '',
  );
  const [effectiveDate, setEffectiveDate] = useState(
    () => initial?.effectiveDate ?? todayIso(),
  );
  const [amount, setAmount] = useState(initial?.amountInr ?? '');
  const [method, setMethod] = useState<PaymentMethod>(
    initial?.method ?? prefs.method ?? 'bank_transfer',
  );
  const [viaSelf, setViaSelf] = useState<boolean>(
    initial?.viaSelf ?? prefs.viaSelf ?? false,
  );
  const [note, setNote] = useState(initial?.note ?? '');
  const [fileEntries, setFileEntries] = useState<FileEntry[]>([]);
  const fileUidRef = useRef(0);
  const [error, setError] = useState<string | null>(null);
  // Once the payment is created we lock it here to prevent accidental
  // double-submission if the user retries after an attachment error.
  const [createdPayment, setCreatedPayment] = useState<Payment | null>(null);
  // Resolved on first upload attempt and cached so retries don't refetch.
  const payerEntryIdRef = useRef<number | null>(null);

  const setFileStatus = (uid: string, status: FileStatus): void => {
    setFileEntries((prev) =>
      prev.map((f) => (f.uid === uid ? { ...f, status } : f)),
    );
  };

  const resolvePayerEntryId = async (payment: Payment): Promise<number> => {
    if (payerEntryIdRef.current !== null) return payerEntryIdRef.current;
    const entries = await api.get<LedgerEntry[]>(`/api/payments/${payment.id}/entries`);
    const entry = entries.find((e) => e.partyId === payment.payerPartyId);
    if (!entry) {
      throw new Error(
        `Payment #${payment.id} was recorded but receipts couldn't be attached ` +
        `(payer ledger entry not found). Close this dialog and add them from the ledger page.`,
      );
    }
    payerEntryIdRef.current = entry.id;
    return entry.id;
  };

  /**
   * Upload a single file with progress tracking. Updates the per-file
   * status as the upload progresses; resolves true on success, false on
   * failure (status already painted by the caller's reaction).
   */
  const uploadOne = async (
    entry: FileEntry,
    entryId: number,
  ): Promise<boolean> => {
    setFileStatus(entry.uid, { kind: 'uploading', pct: 0 });
    try {
      const result = await uploadFile<{ attachment: { id: number } }>(
        `/api/ledger-entries/${entryId}/attachments`,
        entry.file,
        {
          onProgress: (pct) =>
            setFileStatus(entry.uid, { kind: 'uploading', pct }),
        },
      );
      setFileStatus(entry.uid, { kind: 'done', id: result.attachment.id });
      return true;
    } catch (err) {
      const message =
        err instanceof UploadError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Upload failed';
      setFileStatus(entry.uid, { kind: 'failed', message });
      return false;
    }
  };

  const partiesById = useMemo(
    () => new Map((parties.data ?? []).map((p) => [p.id, p])),
    [parties.data],
  );
  const payer = payerPartyId ? partiesById.get(Number(payerPartyId)) : undefined;
  const payee = payeePartyId ? partiesById.get(Number(payeePartyId)) : undefined;
  const sameParty = payerPartyId !== '' && payerPartyId === payeePartyId;

  const createMut = useMutation({
    mutationFn: async (): Promise<{ payment: Payment; allUploaded: boolean }> => {
      // Guard: if the payment was already created (e.g. user retried after an
      // attachment error) skip straight to re-uploading attachments.
      const payment: Payment = createdPayment ?? await (async () => {
        if (!payerPartyId || !payeePartyId) throw new Error('Pick both payer and payee.');
        if (sameParty) throw new Error('Payer and payee must differ.');
        if (!amount.trim()) throw new Error('Amount is required.');
        if (!effectiveDate) throw new Error('Effective date is required.');
        const kinrAmount = inrToKinrString(amount);
        if (kinrAmount === null) throw new Error('Amount is not a valid number.');
        const p = await api.post<Payment>('/api/payments', {
          effectiveDate,
          amount: kinrAmount,
          method,
          payerPartyId: Number(payerPartyId),
          payeePartyId: Number(payeePartyId),
          viaSelf,
          note: note.trim() || null,
        });
        setCreatedPayment(p);
        return p;
      })();

      // Attach receipts to the payer-side ledger entry. Each file is
      // uploaded independently via Promise.allSettled — a failure on one
      // doesn't abort the others, and the per-file status state shows the
      // user exactly which ones succeeded vs. failed.
      const candidates = fileEntries.filter(
        (e) => e.status.kind === 'pending' || e.status.kind === 'failed',
      );
      if (candidates.length === 0) {
        return { payment, allUploaded: true };
      }
      const entryId = await resolvePayerEntryId(payment);
      const results = await Promise.allSettled(
        candidates.map((e) => uploadOne(e, entryId)),
      );
      const allDone = results.every(
        (r) => r.status === 'fulfilled' && r.value === true,
      );
      return { payment, allUploaded: allDone };
    },
    onSuccess: ({ payment, allUploaded }) => {
      // Remember user prefs only when at least the payment landed.
      writePrefs({
        method,
        viaSelf,
        lastPayerPartyId: preselectPayerId ? undefined : payerPartyId || undefined,
        lastPayeePartyId: preselectPayeeId ? undefined : payeePartyId || undefined,
      });
      void qc.invalidateQueries();
      // Only auto-close when every file uploaded successfully. If any
      // failed, keep the dialog open with the per-file Retry affordance.
      if (allUploaded) {
        onCreated(payment);
      }
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const retryFailed = async (): Promise<void> => {
    if (!createdPayment) return;
    const entryId = await resolvePayerEntryId(createdPayment);
    const failed = fileEntries.filter((e) => e.status.kind === 'failed');
    if (failed.length === 0) return;
    const results = await Promise.allSettled(
      failed.map((e) => uploadOne(e, entryId)),
    );
    const stillFailing = results.some(
      (r) => r.status === 'fulfilled' && r.value === false,
    );
    if (!stillFailing) {
      void qc.invalidateQueries();
      onCreated(createdPayment);
    }
  };

  const hasFailedFiles = fileEntries.some((e) => e.status.kind === 'failed');

  return (
    <Modal onClose={onClose} size="xl">
        <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
          <h2 className="text-small font-semibold text-ink">Record payment</h2>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </header>

        <form
          className="flex-1 overflow-y-auto p-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            createMut.mutate();
          }}
        >
          {initial && (
            <div className="mb-3 rounded-md border border-sky-200 bg-sky-50 p-3 text-caption text-sky-700">
              Prefilled from the entry you just voided — change what was wrong,
              then save the corrected payment.
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              label="Payer"
              value={payerPartyId}
              onChange={setPayerPartyId}
              options={(parties.data ?? []).map((p) => ({
                value: String(p.id),
                label: `${p.displayName} (${p.role})`,
              }))}
              placeholder="Choose…"
            />
            <Select
              label="Payee"
              value={payeePartyId}
              onChange={setPayeePartyId}
              options={(parties.data ?? []).map((p) => ({
                value: String(p.id),
                label: `${p.displayName} (${p.role})`,
              }))}
              placeholder="Choose…"
            />
            <TextInput
              label="Effective date"
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
            <div className="flex flex-col">
              <TextInput
                label="Amount (₹ INR)"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 50000"
                inputMode="decimal"
                hint="Positive. Enter amount in rupees — stored internally as K-INR."
              />
              {amount.trim().length > 0 && (
                <p className={`mt-1 text-caption ${
                  inrToKinrPreview(amount) ? 'text-pos' : 'text-neg'
                }`}>
                  {inrToKinrPreview(amount) ?? 'Not a valid amount'}
                  {inrToKinrPreview(amount) && (
                    <span className="ml-1 text-ink-muted">(stored value)</span>
                  )}
                </p>
              )}
            </div>
            <Select
              label="Method"
              value={method}
              onChange={(v) => setMethod(v as PaymentMethod)}
              options={METHODS.map((m) => ({ value: m, label: m }))}
            />
            <div className="col-span-full">
              <TextArea
                label="Note (appears in the ledger description)"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. C0F999 VICKY"
              />
            </div>
          </div>

          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3">
            <Checkbox
              label="This is a three-party pass-through (paid directly on my behalf)"
              hint="Check this if the payer paid the payee directly and the transaction nets through me without hitting my self ledger."
              checked={viaSelf}
              onChange={setViaSelf}
            />
            {viaSelf && (
              <div className="mt-3 text-caption text-amber-700">
                <div className="mb-2">Preview of what will happen:</div>
                <ul className="ml-4 list-disc space-y-1">
                  <li>
                    <strong>{payer?.displayName ?? '(payer)'}</strong>&apos;s ledger: Cr {inrToKinrString(amount) ?? '0'} K-INR
                  </li>
                  <li>
                    <strong>{payee?.displayName ?? '(payee)'}</strong>&apos;s ledger: Dr {inrToKinrString(amount) ?? '0'} K-INR
                  </li>
                  <li>My self ledger: no entry (net zero).</li>
                </ul>
              </div>
            )}
            {!viaSelf && payer && payee && (
              <div className="mt-3 text-caption text-ink-muted">
                Preview:
                <span className="ml-1">
                  {payer.displayName}: Cr {inrToKinrString(amount) ?? '0'} · {payee.displayName}: Dr {inrToKinrString(amount) ?? '0'} K-INR
                </span>
              </div>
            )}
          </div>

          <div className="mt-4">
            <label className="block text-caption font-medium text-ink">
              Attach receipt(s) (optional)
            </label>
            <input
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              className="mt-1 block w-full text-caption text-ink-muted file:mr-3 file:cursor-pointer file:rounded-md file:border file:border-border file:bg-surface-raised file:px-3 file:py-1 file:text-small file:text-ink"
              onChange={(e) => {
                if (!e.target.files) return;
                const added: FileEntry[] = Array.from(e.target.files).map((f) => ({
                  uid: `f${++fileUidRef.current}`,
                  file: f,
                  status: { kind: 'pending' },
                }));
                setFileEntries((prev) => [...prev, ...added]);
              }}
            />
            {fileEntries.length > 0 && (
              <ul className="mt-2 space-y-1 text-caption">
                {fileEntries.map((e) => (
                  <li
                    key={e.uid}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="flex min-w-0 items-center gap-2 text-ink-muted">
                      <Pill>{(e.file.size / 1024).toFixed(0)} kB</Pill>
                      <span className="truncate">{e.file.name}</span>
                      <FileStatusBadge status={e.status} />
                    </span>
                    <span className="shrink-0">
                      {e.status.kind === 'failed' && createdPayment && (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            void (async () => {
                              const entryId = await resolvePayerEntryId(createdPayment);
                              await uploadOne(e, entryId);
                            })();
                          }}
                        >
                          Retry
                        </Button>
                      )}
                      {e.status.kind !== 'uploading' &&
                        e.status.kind !== 'done' && (
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() =>
                              setFileEntries((prev) =>
                                prev.filter((x) => x.uid !== e.uid),
                              )
                            }
                          >
                            Remove
                          </Button>
                        )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && <div className="mt-3"><ErrorBanner>{error}</ErrorBanner></div>}

          <div className="mt-4 flex items-center gap-2">
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? (
                <Spinner />
              ) : createdPayment && hasFailedFiles ? (
                'Retry all failed'
              ) : createdPayment ? (
                'Done'
              ) : (
                'Record payment'
              )}
            </Button>
            {createdPayment && hasFailedFiles && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => void retryFailed()}
              >
                Retry failed
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </form>
    </Modal>
  );
}

function todayIso(): string {
  const d = new Date();
  const y = d.getUTCFullYear().toString().padStart(4, '0');
  const m = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = d.getUTCDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function FileStatusBadge({ status }: { status: FileStatus }) {
  switch (status.kind) {
    case 'pending':
      return <span className="text-ink-muted">Ready</span>;
    case 'uploading':
      return (
        <span className="font-mono text-brand">
          {status.pct.toString().padStart(2, ' ')}%
        </span>
      );
    case 'done':
      return <span className="text-pos">✓ uploaded</span>;
    case 'failed':
      return (
        <span className="text-neg" title={status.message}>
          ⚠ failed
        </span>
      );
    default:
      return null;
  }
}
